import {
	DateObject,
	type FanShopProductResponseLite,
	type FanShopProductVariant,
	type FrontAuctionDTO,
	isWeb,
	useIntl,
	useSettings
} from '@capsicum/common';
import { hasEnoughTokenCombinedWallet, useWalletFanToken } from '@capsicum/features/wallet/shared';
import {
	type NCWBalanceType,
	type NCWTokenBalanceType,
	useMe,
	usePartners,
	useWalletsBalanceWrapper
} from '@capsicum/socios-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ProductTypes } from '../../models';
import { getPriceForVariant } from '../../util';
import { useCategories } from '../useCategories';
import { useRPAuctionsCommon } from '../useRPAuctionsCommon';

type UseRPAuctionCardProps = {
	getTokenBalanceBy?: NCWBalanceType['getTokenBalanceBy'];
	variant: FrontAuctionDTO;
};

export const useRPAuctionCard = ({ getTokenBalanceBy = () => undefined, variant }: UseRPAuctionCardProps) => {
	const [isComingSoon, setIsComingSoon] = useState<boolean>();
	const { $t, getLocalizedField } = useIntl();
	const { getPartnerBy } = usePartners();
	const { me } = useMe();
	const currentPartner = useMemo(
		() => getPartnerBy(p => p.id === variant?.product?.partnerId),
		[getPartnerBy, variant?.product?.partnerId]
	);
	const { getCurrencyBy } = useWalletsBalanceWrapper();
	const partnerCurrencyId = currentPartner?.currencyId;
	const tokensRequired = useMemo(() => variant?.product?.productTierLevel?.minTokenRequired || 0, [variant]);
	const {
		settings: { isPoblanoEnabled, preventBuyFanTokensModal }
	} = useSettings();

	const { currentStatus, setCurrentStatus } = useRPAuctionTimer(variant);

	const { getSubcategoryBy } = useCategories();
	const { data: partnerWallet, isPending: isPendingCWallet } = useWalletFanToken(partnerCurrencyId);
	const { hasEnoughToken } = partnerWallet ?? {};

	const getMainImage = useCallback(
		(v: FanShopProductVariant = variant || {}) => {
			const checkImages = (objWithImages?: FanShopProductResponseLite | FanShopProductVariant) => {
				return objWithImages?.images
					?.filter(image =>
						isWeb()
							? image?.imageType?.type?.includes('desktop')
							: !image?.imageType?.type?.includes('desktop')
					)
					?.find(image => image?.imageType?.type?.includes('_image_'));
			};

			return checkImages(v) || checkImages(v?.product);
		},
		[variant]
	);

	const partnerCurrency = useMemo(
		() => getCurrencyBy(c => c.currency.id === partnerCurrencyId),
		[getCurrencyBy, partnerCurrencyId]
	);
	const NCWPartnerTokens = useMemo(
		() => getTokenBalanceBy((p: NCWTokenBalanceType) => p.shortCode === partnerCurrency?.currency.shortCode),
		[getTokenBalanceBy, partnerCurrency?.currency.shortCode]
	);
	const balance = useMemo(
		() => Number(partnerWallet?.totalBalance || 0) + Number(NCWPartnerTokens?.amount || 0),
		[partnerWallet?.totalBalance, NCWPartnerTokens?.amount]
	);

	const hasEnoughFanTokens = useCallback(() => {
		if (isPoblanoEnabled) {
			return hasEnoughTokenCombinedWallet(tokensRequired, balance);
		} else {
			return hasEnoughToken?.(tokensRequired);
		}
	}, [tokensRequired, balance, hasEnoughTokenCombinedWallet, hasEnoughToken]);

	const getAuctionSubcategory = useMemo(
		() => getSubcategoryBy(s => s?.id === variant?.product?.categories?.[0]?.id),
		[getSubcategoryBy, variant]
	);

	const auctionTitle = useMemo(() => variant?.label || '', [variant]);

	const auctionPrice = useMemo(
		() =>
			variant?.latestAuctionBid
				? Math.trunc(Number(variant?.latestAuctionBid.amount))
				: getPriceForVariant(variant?.sellingPrice, variant?.product?.sellingPrice),
		[variant]
	);
	const auctionImage = useMemo(() => getMainImage(variant)?.url || '', [variant, getMainImage]);
	const auctionBlurHash = useMemo(() => getMainImage(variant)?.blurHash || '', [variant, getMainImage]);
	const auctionRibbonColor = getAuctionSubcategory?.color;
	const auctionRibbonText = getLocalizedField('name', getAuctionSubcategory || {}) || getAuctionSubcategory?.name;

	const tokenRequiredAmount = variant?.product?.productTierLevel?.minTokenRequired || 0;

	const auctionPartnerId = useMemo(() => variant?.product?.partnerId, [variant]);
	const isTicketType = variant?.product?.productType?.name === ProductTypes.Ticket;

	const redeemableAt = variant?.redeemableAt;
	const endAt = variant?.endAt;

	useEffect(() => {
		if (redeemableAt && new DateObject(redeemableAt).getTime() > DateObject.now()) {
			setIsComingSoon(true);
		}
	}, [redeemableAt]);

	const amountNeeded = useMemo(
		() => (tokensRequired - balance > 0 ? tokensRequired - balance : 0),
		[tokensRequired, balance]
	);

	const texts = useMemo(
		() => ({
			availableIn: $t('rewards.fanshop.availableIn'),
			comingSoon: $t('rewards.fanshop.comingSoon'),
			currentBid: $t('rewards.rpAuctions.currentBid'),
			fanTokensNeededText: $t('marketplace.tokenBalance.fanTokensNeeded'),
			getFanTokensText: $t('marketplace.tokenBalance.getFanTokens', {
				shortName: currentPartner?.shortName
			}),
			minimumAmount: `${amountNeeded} ${currentPartner?.shortName}`,
			startingBid: $t('rewards.rpAuctions.startingBid'),
			winningBid: $t('rewards.rpAuctions.winningBid'),
			yourBid: $t('rewards.rpAuctions.yourBid'),
			yourWinningBid: $t('rewards.rpAuctions.yourWinningBid')
		}),
		[$t, amountNeeded, variant, currentPartner]
	);

	const lastBidderId = variant?.latestAuctionBid?.userReference?.chilizUserId;

	const isOwnBid = me.externalUserId === lastBidderId;

	const { bidTitle } = useRPAuctionsCommon({ isAuctionPage: false, isOwnBid: isOwnBid, variant });

	return {
		amountNeeded,
		auctionBlurHash,
		auctionImage,
		auctionPartnerId,
		auctionPrice,
		auctionRibbonColor,
		auctionRibbonText,
		auctionTitle,
		balance,
		bidTitle,
		currentPartner,
		currentStatus,
		endAt,
		hasEnoughFanTokens,
		isComingSoon,
		isOwnBid,
		isPendingCWallet,
		isTicketType,
		partnerCurrencyId,
		partnerWallet,
		preventBuyFanTokensModal,
		redeemableAt,
		setCurrentStatus,
		setIsComingSoon,
		texts,
		tokenRequiredAmount
	};
};

export const useRPAuctionTimer = (auction: FrontAuctionDTO | undefined) => {
	const [currentStatus, setCurrentStatus] = useState<string>('');

	//#region for usage inside the flashlist
	// https://shopify.github.io/flash-list/docs/recycling
	const lastAuctionId = useRef(auction?.id);

	if (auction?.id !== lastAuctionId.current) {
		lastAuctionId.current = auction?.id;
		setCurrentStatus('');
	}
	//#endregion

	return {
		currentStatus,
		setCurrentStatus
	};
};
